
  import { Component, Vue, Watch } from 'vue-property-decorator'
  import { Role } from '@/store/user/models'
  import { mapActions, mapGetters } from 'vuex'
  import ChangeLogDialog from '@/components/ChangeLogDialog.vue'
  import CircularImage from '@/components/tutorial/CircularImage.vue'
  import BasicTourExample from '@/plugins/tour/examples/BasicTourExample.vue'
  import { WelcomeTour } from '@/plugins/tour/examples/WelcomeTour'
  import { FinancerWelcomeTour } from '@/plugins/tour/examples/FinancerWelcomeTour'
  import { isTourCompleted } from '@/plugins/tour/utils/helpers'
  import { User } from '@/entities/public'

@Component({
  components: {
    CircularImage,
    ChangeLogDialog,
    BasicTourExample,
  },
  computed: {
    ...mapGetters('user', ['roles', 'rolesName']),
    ...mapGetters('app', ['isMobile']),
    ...mapGetters('user', ['onlineUsersSubscription']),
  },
  methods: {
    ...mapActions('user', ['subscribeToAllUsersLastSeen']),
  },
})
  export default class Home extends Vue {
  subscribeToAllUsersLastSeen!: () => Promise<any>
  private onlineUsersSubscription: User[]
  isMobile!: boolean
  roles!: Array<Role>
  rolesName!: Array<string>
  img = require('@/assets/genio/welcome.svg')
  displayOnlineUsers = false

  // Flag para mostrar el botón de inicio del tour
  showTourButton = true // Forzado a true durante desarrollo

  // Variables para el diálogo de bienvenida
  showWelcomeDialog = false
  tourSkipped = false
  welcomeDialogTitle = 'Bienvenido a GENIO'
  welcomeDialogMessage= 'Estoy aquí para ayudarte a ganar mas y complicarte menos.'

  get width () {
    const { isMobile } = this

    return isMobile ? 250 : 300
  }

  get height () {
    const { isMobile } = this
    return isMobile ? 250 : 300
  }

  get calculatedSize () {
    if (this.isMobile) return 400

    // Base size para resoluciones estándar (1920px)
    const baseSize = 650

    // Obtener el ancho actual de la pantalla
    const screenWidth = this.$vuetify.breakpoint.width
    // Para pantallas de alta resolución (más de 1920px), escalar proporcionalmente
    if (screenWidth > 1700) {
      // Factor de escala: 10% más grande por cada 500px adicionales
      const scaleFactor = 1 + ((screenWidth - 1920) / 500) * 0.1
      // Limitar el factor de escala a un máximo de 1.5 (50% más grande)
      return Math.min(baseSize * scaleFactor, baseSize * 1.5)
    }

    return baseSize
  }

  get menu () {
    const { isMobile } = this

    if (isMobile) {
      return [
        { icon: '$lead', process: 'lead', tooltip: 'Leads', link: 'leads' },
        { icon: '$appraisal', process: 'appraisal', tooltip: 'Tasaciones', link: 'tasaciones' },
        { icon: '$inspection', process: 'inspection', tooltip: 'Inspecciones', link: 'inspección' },
        { icon: '$negotiation_icon', process: 'negotiation', tooltip: 'Negociaciones', link: 'negociación' },
        { icon: '$purchase', process: 'purchase', tooltip: 'Compra', link: 'compra' },
        { icon: '$enablement', process: 'enablement', tooltip: 'Habilitación', link: 'habilitación' },
        { icon: '$kpi_icon', process: 'kpi', tooltip: 'KPIs', link: 'kpi' },
        { icon: '$publications', process: 'publications', tooltip: 'Publicaciones', link: 'publicaciones' },
      ]
    }

    return [
          { icon: '$lead', process: 'lead', tooltip: 'Leads', link: 'leads' },
          { icon: '$appraisal', process: 'appraisal', tooltip: 'Tasaciones', link: 'tasaciones' },
          { icon: '$inspection', process: 'inspection', tooltip: 'Inspecciones', link: 'inspección' },
          { icon: '$negotiation_icon', process: 'negotiation', tooltip: 'Negociaciones', link: 'negociación' },
          { icon: '$purchase', process: 'purchase', tooltip: 'Compra', link: 'compra' },
          { icon: '$enablement', process: 'enablement', tooltip: 'Habilitación', link: 'habilitación' },
          { icon: '$kpi_icon', process: 'kpi', tooltip: 'KPIs', link: 'kpi' },
          { icon: '$publications', process: 'publications', tooltip: 'Publicaciones', link: 'publicaciones' },
          { icon: '$reserve', process: 'reserve', tooltip: 'Reservas', link: 'reservas' },
          { icon: '$financing', process: 'financing', tooltip: 'Financiamientos', link: 'financiamiento' },
          { icon: '$treasurer', process: 'treasure', tooltip: 'Tesorería', link: 'tesorería' },
          { icon: '$all', process: 'all', tooltip: 'Todos', link: 'todos' },
          { icon: '$sell', process: 'sell', tooltip: 'Ventas', link: 'venta' },
        ]
  }

  get isFinancingOnly () {
    return this.roles.some(role => role.slug === 'financer') && this.roles.length === 1
  }

  /**
   * Obtiene el tour apropiado según el rol del usuario
   */
  get currentTour () {
    return this.isFinancingOnly ? FinancerWelcomeTour : WelcomeTour
  }

  /**
   * Obtiene el ID del tour actual
   */
  get currentTourId () {
    return this.currentTour.id
  }

  /**
   * Obtiene las claves de localStorage específicas para el tour actual
   */
  get tourStorageKeys () {
    if (this.isFinancingOnly) {
      return {
        completed: 'financer-welcome-tour-completed',
        skipped: 'financer-welcome-tour-skipped',
        justFinished: 'financer-tour-just-finished',
      }
    }
    return {
      completed: 'welcome-tour-completed',
      skipped: 'welcome-tour-skipped',
      justFinished: 'tour-just-finished',
    }
  }

  startTour () {
    // Limpiar cualquier estado previo de manera exhaustiva
    this.resetTourStateCompletely()

    // Forzar reinicio del tour
    if (this.isFinancingOnly) {
      this.forceRestartFinancerWelcomeTour()
    } else {
      this.forceRestartWelcomeTour()
    }
  }

  /**
   * Inicia el Tour de Bienvenida usando el componente global
   */
  startWelcomeTour () {
    if (this.isFinancingOnly) {
      return
    }

    if (this.$globalTour) {
      this.$globalTour.startWelcomeTour()
    } else {
      console.error('❌ Error: GlobalTour no está disponible')
    }
  }

  /**
   * Fuerza el reinicio del Tour de Bienvenida
   */
  forceRestartWelcomeTour () {
    if (this.isFinancingOnly) {
      return
    }

    if (this.$globalTour) {
      // Primero finalizar cualquier tour activo
      this.$globalTour.endTour()

      // Usar setTimeout para asegurar que el tour anterior termine
      setTimeout(() => {
        this.$globalTour.startWelcomeTour()
      }, 100)
    } else {
      console.error('❌ Error: GlobalTour no está disponible')
    }
  }

  /**
   * Inicia el Tour de Bienvenida específico para financer
   */
  startFinancerWelcomeTour () {
    if (!this.isFinancingOnly) {
      return
    }

    if (this.$globalTour) {
      if (typeof this.$globalTour.startFinancerWelcomeTour === 'function') {
        this.$globalTour.startFinancerWelcomeTour()
      } else {
        this.$globalTour.startTour(this.currentTour)
      }
    } else {
      console.error('❌ Error: GlobalTour no está disponible')
    }
  }

  /**
   * Fuerza el reinicio del Tour de Bienvenida específico para financer
   */
  forceRestartFinancerWelcomeTour () {
    if (!this.isFinancingOnly) {
      return
    }

    if (this.$globalTour) {
      // Primero finalizar cualquier tour activo
      this.$globalTour.endTour()

      // Usar setTimeout para asegurar que el tour anterior termine
      setTimeout(() => {
        if (typeof this.$globalTour.startFinancerWelcomeTour === 'function') {
          this.$globalTour.startFinancerWelcomeTour()
        } else {
          this.$globalTour.startTour(this.currentTour)
        }
      }, 100)
    } else {
      console.error('❌ Error: GlobalTour no está disponible')
    }
  }

  /**
   * Verifica el estado del tour y decide si mostrarlo
   */
  checkTourStatus () {
    const storageKeys = this.tourStorageKeys
    const currentTourId = this.currentTourId

    // Verificar explícitamente si el tour ya fue completado
    const explicitCompleted = localStorage.getItem(storageKeys.completed) === 'true'

    // Si está marcado como completado explícitamente, no hacer nada más
    if (explicitCompleted) {
      this.showTourButton = false
      return
    }

    this.checkAndStartTourIfNeeded(storageKeys, currentTourId)
  }

  /**
   * Verifica las condiciones y inicia el tour si es necesario
   */
  checkAndStartTourIfNeeded (storageKeys: any, currentTourId: string) {
    // Verificaciones originales
    const tourCompleted = isTourCompleted(currentTourId)
    const tourSkipped = localStorage.getItem(storageKeys.skipped) === 'true'
    const justFinishedTour = localStorage.getItem(storageKeys.justFinished) === 'true'

    // Si acabamos de finalizar el tour, no lo iniciamos de nuevo
    if (justFinishedTour) {
      localStorage.removeItem(storageKeys.justFinished)
      return
    }

    // Si ya fue completado o omitido, no mostramos el botón
    this.showTourButton = !tourCompleted && !tourSkipped

    this.startTourForNewUsers(tourCompleted, tourSkipped, currentTourId)
  }

  /**
   * Inicia el tour para usuarios nuevos que nunca lo han visto
   */
  startTourForNewUsers (tourCompleted: boolean, tourSkipped: boolean, currentTourId: string) {
    const explicitCompleted = localStorage.getItem(this.tourStorageKeys.completed) === 'true'

    if (!tourCompleted && !tourSkipped && !explicitCompleted && this.$globalTour) {
      const localStorageTourHistory = localStorage.getItem('tourHistory')
      const hasSeenTour = localStorageTourHistory && localStorageTourHistory.includes(currentTourId)

      if (!hasSeenTour) {
        setTimeout(() => {
          if (this.isFinancingOnly) {
            this.startFinancerWelcomeTour()
          } else {
            this.startWelcomeTour()
          }
        }, 1500)
      }
    }
  }

  /**
   * Restablece el estado del tour para pruebas
   */
  resetTourState () {
    // Limpiar estados generales
    localStorage.removeItem('tourHistory')

    // Limpiar estados del tour general
    localStorage.removeItem('welcome-tour-completed')
    localStorage.removeItem('welcome-tour-skipped')
    localStorage.removeItem('tour-just-finished')

    // Limpiar estados del tour financer
    localStorage.removeItem('financer-welcome-tour-completed')
    localStorage.removeItem('financer-welcome-tour-skipped')
    localStorage.removeItem('financer-tour-just-finished')
  }

  /**
   * Restablece completamente el estado del tour de manera exhaustiva
   */
  resetTourStateCompletely () {
    // Ejecutar la limpieza original
    this.resetTourState()

    // Limpiar estados adicionales que puedan interferir
    const tourKeys = [
      'tourHistory',
      'welcome-tour-completed',
      'welcome-tour-skipped',
      'tour-just-finished',
      'financer-welcome-tour-completed',
      'financer-welcome-tour-skipped',
      'financer-tour-just-finished',
    ]

    // Limpiar cada clave de manera explícita
    tourKeys.forEach(key => {
      try {
        localStorage.removeItem(key)
      } catch (error) {
        console.warn(`No se pudo limpiar ${key}:`, error)
      }
    })

    // También limpiar cualquier estado del store de tours si existe
    if (this.$store && this.$store.state.tour) {
      try {
        this.$store.dispatch('tour/toggleTourEnabled', true)
      } catch (error) {
        console.warn('No se pudo restablecer el estado del tour en el store:', error)
      }
    }
  }

  /**
   * Configura el gestor de tours basados en roles
   */
  setupRoleTourManager () {
    if (this.$roleTourManager && this.rolesName) {
      // Configurar los roles del usuario actual
      this.$roleTourManager.setUserRoles(this.rolesName)
    }
  }

  /**
   * Manejador del evento de finalización del tour
   */
  handleTourFinish (event: CustomEvent) {
    const storageKeys = this.tourStorageKeys
    const currentTourId = this.currentTourId

    this.tourSkipped = event.detail?.skipped || false

    if (this.tourSkipped) {
      this.welcomeDialogTitle = 'Tour Omitido'
      this.welcomeDialogMessage = 'Has omitido el tour de bienvenida. Puedes volver a iniciarlo cuando quieras haciendo clic en el botón de ayuda.'
    } else {
      this.welcomeDialogTitle = 'Bienvenido a GENIO'
    }

    // Marcar explícitamente que el tour ha sido completado para evitar reinicio
    localStorage.setItem('tourHistory', JSON.stringify([currentTourId]))
    localStorage.setItem(storageKeys.completed, 'true')

    this.showWelcomeDialog = true
  }

  async mounted () {
    await this.subscribeToAllUsersLastSeen()
    // Verificar estado del tour
    this.checkTourStatus()

    // Configurar el gestor de tours basados en roles
    this.setupRoleTourManager()

    // Agregar listener para el evento de finalización del tour
    document.addEventListener('finish-welcome-tour', this.handleTourFinish as (event: Event) => void)
  }

  beforeDestroy () {
    // Eliminar el listener al destruir el componente
    document.removeEventListener('finish-welcome-tour', this.handleTourFinish as (event: Event) => void)
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange () {
    // Mostrar usuarios en línea si el parámetro dev=true está presente
    this.displayOnlineUsers = this.$route.query.dev === 'true'
  }
  }
